import { Controller } from "@hotwired/stimulus";

/**
 * Auto-dismisses a toast after a configurable delay, optionally fading out first.
 * Removes the element from the DOM after the fade so it cannot block clicks.
 *
 * @example
 *   <div data-controller="toast"
 *        data-toast-dismiss-after-value="3000"
 *        data-toast-fade-class="tw-opacity-0"
 *        data-toast-fade-duration-value="200">
 *     Saved!
 *   </div>
 */
export default class extends Controller {
  static classes = ["fade"];
  static values = {
    dismissAfter: { type: Number, default: 0 },
    fadeDuration: { type: Number, default: 200 },
  };

  declare dismissAfterValue: number;
  declare fadeDurationValue: number;
  declare fadeClass: string;
  declare hasFadeClass: boolean;
  declare dismissTimeoutId?: ReturnType<typeof setTimeout>;
  declare removeTimeoutId?: ReturnType<typeof setTimeout>;
  private removed = false;

  /** Starts the auto-dismiss timer when +dismissAfter+ is greater than zero. */
  connect(): void {
    if (this.dismissAfterValue > 0) {
      this.dismissTimeoutId = setTimeout(
        () => this.fadeOut(),
        this.dismissAfterValue
      );
    }
  }

  /** Clears pending timers; removes a faded toast so it cannot block the UI. */
  disconnect(): void {
    this.clearTimers();

    if (
      this.removed ||
      !this.element.isConnected ||
      this.dismissAfterValue <= 0
    ) {
      return;
    }

    if (this.hasFadeClass && this.element.classList.contains(this.fadeClass)) {
      this.removeElement();
    }
  }

  /** Removes the toast immediately and clears any pending timers. */
  dismiss(): void {
    this.clearTimers();
    this.removeElement();
  }

  private clearTimers(): void {
    if (this.dismissTimeoutId) {
      clearTimeout(this.dismissTimeoutId);
      this.dismissTimeoutId = undefined;
    }
    if (this.removeTimeoutId) {
      clearTimeout(this.removeTimeoutId);
      this.removeTimeoutId = undefined;
    }
  }

  /** Applies the fade class, then removes the element after the transition. */
  private fadeOut(): void {
    if (this.removed) return;

    if (!this.hasFadeClass) {
      this.dismiss();
      return;
    }

    const removeAfterFade = (): void => {
      if (this.removed) return;
      this.clearTimers();
      this.removeElement();
    };

    this.element.classList.add(this.fadeClass);
    this.element.style.pointerEvents = "none";

    this.element.addEventListener("transitionend", removeAfterFade, {
      once: true,
    });

    // Fallback when transitionend does not fire (reduced motion, missing transition).
    this.removeTimeoutId = setTimeout(
      removeAfterFade,
      this.fadeDurationValue + 100
    );
  }

  private removeElement(): void {
    if (this.removed) return;
    this.removed = true;

    if (this.element.isConnected) {
      this.element.remove();
    }
  }
}
